import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { getMetaForPath, isProductionHost } from '../seo/routeMeta';

// Keeps <head> correct as the user navigates within the SPA.
//
// The build already ships a correct, fully-formed <head> for every indexable
// route (scripts/prerender-seo.js), so this hook is not what gets pages indexed
// any more -- it exists to keep client-side navigation honest, and to fix up the
// one case the prerenderer cannot: build/index.html doubles as the SPA fallback
// for every unmatched path, so a route like /some-host/some-experience is first
// served the *homepage's* canonical and robots tags. Left as-is, that would tell
// Google an Experience page is the homepage. Below we correct it on mount.
//
// Division of responsibility: this hook only asserts a title/description for
// routes present in routeMeta.js. For anything else the route component owns
// those tags (ExperienceLandingPageV2 and the legal pages set their own), and
// MetaHandler renders ahead of <Routes> so those components' effects run after
// this one and win -- overwriting here would clobber a specific title with a
// generic one.

// Upserts a <meta> tag by attribute, so we update the prerendered tag in place
// rather than appending a second, competing one.
const setMeta = (attribute, key, content) => {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const removeMeta = (attribute, key) => {
  document.head
    .querySelectorAll(`meta[${attribute}="${key}"]`)
    .forEach((element) => element.remove());
};

const setCanonical = (href) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (link) link.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

const usePageMeta = () => {
  const location = useLocation();

  useEffect(() => {
    const meta = getMetaForPath(location.pathname);
    const onProductionHost = isProductionHost(window.location.hostname);

    // QA and preview hosts serve the same bundle as production. Without this
    // guard the branch below would *remove* the noindex the QA build stamped in
    // -- on an indexable route it deletes the robots tag -- and hand Google a
    // crawlable copy of the site on vasi-qa.web.app. Nothing else in <head>
    // matters if the page is excluded, so bail out before touching the rest.
    if (!onProductionHost) {
      setMeta('name', 'robots', 'noindex, nofollow');
      setCanonical(null);
      return;
    }

    // Always correct, because the value inherited from the SPA fallback is the
    // homepage's and is wrong for every route that is not the homepage.
    setCanonical(meta.canonical);

    if (meta.noindex) {
      // follow (not nofollow): we still want link equity to flow from these pages
      // to the public ones they link to -- we just don't want them listed.
      setMeta('name', 'robots', 'noindex, follow');
    } else {
      removeMeta('name', 'robots');
    }

    if (!meta.hasOwnMeta) return;

    document.title = meta.title;
    setMeta('name', 'description', meta.description);
    setMeta('property', 'og:title', meta.title);
    setMeta('property', 'og:description', meta.description);
    setMeta('name', 'twitter:title', meta.title);
    setMeta('name', 'twitter:description', meta.description);
    if (meta.canonical) setMeta('property', 'og:url', meta.canonical);
  }, [location]);
};

export default usePageMeta;
